import type { AutomationProfile } from '../../types';

/**
 * Automation Profiles: Pre-configured rule weighting strategies
 * These profiles adjust how the generator balances different priorities
 */
export const mockAutomationProfiles: AutomationProfile[] = [
  {
    id: 'profile-1',
    name: 'Strict Compliance',
    description: 'Maximize adherence to all rules and preferences, even if it takes longer to generate',
    type: 'strict',
    hardRuleEnforcement: 'always',
    softRuleWeight: 10,
    preferenceWeight: 8,
    teacherPreferenceWeight: 7,
    maxGenerationTime: 300, // 5 minutes
    conflictTolerance: 'none',
    isDefault: false,
    isActive: true,
    settings: {
      prioritizeTeacherWellbeing: true,
      prioritizeStudentWellbeing: true,
      prioritizeRoomEfficiency: true,
      allowSoftRuleViolations: false,
      maxSoftRuleViolations: 0,
    },
    createdAt: new Date('2025-01-01'),
    updatedAt: new Date('2025-01-01'),
  },
  {
    id: 'profile-2',
    name: 'Balanced Optimization',
    description: 'Default profile balancing rule compliance with practical generation time',
    type: 'balanced',
    hardRuleEnforcement: 'always',
    softRuleWeight: 7,
    preferenceWeight: 5,
    teacherPreferenceWeight: 5,
    maxGenerationTime: 120, // 2 minutes
    conflictTolerance: 'minimal',
    isDefault: true,
    isActive: true,
    settings: {
      prioritizeTeacherWellbeing: true,
      prioritizeStudentWellbeing: true,
      prioritizeRoomEfficiency: false,
      allowSoftRuleViolations: true,
      maxSoftRuleViolations: 10,
    },
    createdAt: new Date('2025-01-01'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'profile-3',
    name: 'Quick Generation',
    description: 'Fast generation prioritizing hard rules only, useful for initial drafts',
    type: 'flexible',
    hardRuleEnforcement: 'always',
    softRuleWeight: 3,
    preferenceWeight: 2,
    teacherPreferenceWeight: 2,
    maxGenerationTime: 60, // 1 minute
    conflictTolerance: 'moderate',
    isDefault: false,
    isActive: true,
    settings: {
      prioritizeTeacherWellbeing: false,
      prioritizeStudentWellbeing: true,
      prioritizeRoomEfficiency: false,
      allowSoftRuleViolations: true,
      maxSoftRuleViolations: 25,
    },
    createdAt: new Date('2025-01-01'),
    updatedAt: new Date('2025-01-01'),
  },
  {
    id: 'profile-4',
    name: 'Teacher-Focused',
    description: 'Prioritize teacher preferences and wellbeing over other considerations',
    type: 'strict',
    hardRuleEnforcement: 'always',
    softRuleWeight: 6,
    preferenceWeight: 4,
    teacherPreferenceWeight: 10,
    maxGenerationTime: 180, // 3 minutes
    conflictTolerance: 'minimal',
    isDefault: false,
    isActive: true,
    settings: {
      prioritizeTeacherWellbeing: true,
      prioritizeStudentWellbeing: false,
      prioritizeRoomEfficiency: false,
      allowSoftRuleViolations: true,
      maxSoftRuleViolations: 15,
    },
    createdAt: new Date('2025-01-05'),
    updatedAt: new Date('2025-01-05'),
  },
  {
    id: 'profile-5',
    name: 'Student-Centric',
    description: 'Optimize for student learning conditions and wellbeing',
    type: 'balanced',
    hardRuleEnforcement: 'always',
    softRuleWeight: 9,
    preferenceWeight: 7,
    teacherPreferenceWeight: 3,
    maxGenerationTime: 150, // 2.5 minutes
    conflictTolerance: 'minimal',
    isDefault: false,
    isActive: true,
    settings: {
      prioritizeTeacherWellbeing: false,
      prioritizeStudentWellbeing: true,
      prioritizeRoomEfficiency: false,
      allowSoftRuleViolations: true,
      maxSoftRuleViolations: 8,
    },
    createdAt: new Date('2025-01-08'),
    updatedAt: new Date('2025-01-08'),
  },
  {
    id: 'profile-6',
    name: 'Resource Efficient',
    description: 'Maximize room utilization and minimize facility conflicts',
    type: 'balanced',
    hardRuleEnforcement: 'always',
    softRuleWeight: 6,
    preferenceWeight: 6,
    teacherPreferenceWeight: 4,
    maxGenerationTime: 120, // 2 minutes
    conflictTolerance: 'minimal',
    isDefault: false,
    isActive: true,
    settings: {
      prioritizeTeacherWellbeing: false,
      prioritizeStudentWellbeing: false,
      prioritizeRoomEfficiency: true,
      allowSoftRuleViolations: true,
      maxSoftRuleViolations: 12,
    },
    createdAt: new Date('2025-01-10'),
    updatedAt: new Date('2025-01-10'),
  },
  {
    id: 'profile-7',
    name: 'Emergency Draft',
    description: 'Ultra-fast generation for urgent situations, hard rules only',
    type: 'flexible',
    hardRuleEnforcement: 'always',
    softRuleWeight: 1,
    preferenceWeight: 1,
    teacherPreferenceWeight: 1,
    maxGenerationTime: 30, // 30 seconds
    conflictTolerance: 'high',
    isDefault: false,
    isActive: false,
    settings: {
      prioritizeTeacherWellbeing: false,
      prioritizeStudentWellbeing: false,
      prioritizeRoomEfficiency: false,
      allowSoftRuleViolations: true,
      maxSoftRuleViolations: 50,
    },
    createdAt: new Date('2025-01-12'),
    updatedAt: new Date('2025-01-12'),
  },
];

export const getActiveAutomationProfiles = (): AutomationProfile[] => {
  return mockAutomationProfiles.filter((profile) => profile.isActive);
};

export const getDefaultAutomationProfile = (): AutomationProfile | undefined => {
  return mockAutomationProfiles.find((profile) => profile.isDefault);
};

export const getAutomationProfilesByType = (type: AutomationProfile['type']): AutomationProfile[] => {
  return mockAutomationProfiles.filter((profile) => profile.type === type);
};
